import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  postLtcPlanningQuotesQueryRequest,
  selectClient,
  selectLtcPlanningQuotes,
  selectLtcPlanningQuotesIsLoading,
  selectMaybeClientPartner,
} from '@client/model';
import { selectLtcFinancialPlanningSelectedPolicyTypes } from '@client/model/selectors';
import { FundingPolicyType } from '@shared';
import { calcClientDemographics } from '@shared/calcClientDemographics';
import { fundingPolicyTypeDefs } from '@shared/FundingPolicyTypeDef';

interface UseLtcPlanningQuotesOptions {
  enabled?: boolean;
}

export function useLtcPlanningQuotes(options: UseLtcPlanningQuotesOptions = {}) {
  const { enabled = true } = options;

  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const allQuotes = useSelector(selectLtcPlanningQuotes);
  const isLoading = useSelector(selectLtcPlanningQuotesIsLoading);

  const selectedPolicyTypes = useSelector(selectLtcFinancialPlanningSelectedPolicyTypes);

  const { demographicsData } = useMemo(() => {
    const demographics = calcClientDemographics(client.intakeSurvey);

    const demographicsData: FetchQuotesDemographics = {
      primaryAge: demographics.primaryAge,
      primaryGender: demographics.primaryGender,
      primaryTobaccoStatus: demographics.primaryTobaccoStatus,
      usaState: demographics.state?.stateAbbreviation ?? '',
      maritalStatus: client.intakeSurvey.clientMaritalStatus,
    };

    // Include partner demographics if partner exists (for joint policy queries)
    if (clientPartner?.intakeSurvey) {
      const partnerDemographics = calcClientDemographics(clientPartner.intakeSurvey);
      demographicsData.secondaryAge = partnerDemographics?.primaryAge;
      demographicsData.secondaryGender = partnerDemographics?.primaryGender;
      demographicsData.secondaryTobaccoStatus = partnerDemographics?.primaryTobaccoStatus;
    }

    return { demographicsData };
  }, [client.intakeSurvey, clientPartner?.intakeSurvey]);

  // Fetch quotes on mount if enabled
  useEffect(() => {
    if (!enabled || !client?.intakeSurvey) {
      return;
    }

    // Dispatch request action - API middleware will handle the fetch
    // Server will query all applicable policy types based on maritalStatus
    dispatch(
      postLtcPlanningQuotesQueryRequest({
        demographics: demographicsData,
        advisorId: client.advisorId,
      }),
    );
  }, [enabled]);

  // Filter quotes by selected policy type categories using calculationType
  // Note: ROI calculations are now done in the enrichLtcPlanningQuotesMiddleware
  // when quotes are fetched, so allQuotes already contains enriched data
  const filteredQuotes = useMemo(() => {
    if (!allQuotes?.length) {
      return [];
    }

    // If no policy types selected, show all quotes
    if (selectedPolicyTypes.length === 0) {
      return allQuotes;
    }

    return allQuotes.filter(quote => {
      const policyDef = fundingPolicyTypeDefs[quote.fundingPolicyType];
      if (!policyDef) return false;

      // Check if this quote's calculationType matches any selected category
      return selectedPolicyTypes.includes(policyDef.calculationType);
    });
  }, [allQuotes, selectedPolicyTypes]);

  // Filter and sort quotes (ROI already calculated by middleware)
  const quotesWithROI = useMemo(() => {
    if (!filteredQuotes.length) {
      return [];
    }

    // Quotes already have ROI, formattedROI, and projectedCostCoverage calculated by middleware
    // Just filter out quotes with no coverage and sort by ROI
    return filteredQuotes
      .filter(q => (q.projectedCostCoverage ?? 0) > 0)
      .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));
  }, [filteredQuotes]);

  // Get policy type label for a quote
  const getPolicyTypeLabel = (fundingPolicyType: FundingPolicyType): string => {
    const policyDef = fundingPolicyTypeDefs[fundingPolicyType];
    return policyDef?.label ?? 'Unknown Policy Type';
  };

  return {
    allQuotes: quotesWithROI,
    isLoading,
    totalCount: quotesWithROI.length,
    selectedPolicyTypes,
    getPolicyTypeLabel,
  };
}
